import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/campaignMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, Anchor, Plus, Trash2, X } from "lucide-react";

const human = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const LABELS = {
  shallow_water_capability: "Shallow-Water Capability",
  luxury_finish: "Luxury / Finish",
  payment_affordability: "Payment / Affordability",
  technology_electronics: "Technology / Electronics",
  ease_of_ownership: "Ease of Ownership",
  oem_support: "OEM Support / Incentives",
  prior_model_year: "Prior Model Year",
  prior_model_year_cleanup: "Prior Model Year Cleanup",
  carrying_cost_reduction: "Carrying Cost Reduction",
  inventory_turns: "Inventory Turns",
};
const optLabel = (o) => LABELS[o] || human(o);

const USE_CASES = ["inshore_fishing", "offshore_fishing", "freshwater_fishing", "cruising",
  "family_recreation", "watersports", "pontoon_recreation", "performance_boating"];
const MARKET_TYPES = ["saltwater", "freshwater", "coastal", "lake", "river"];
const INV_PRIORITIES = ["oldest_inventory", "carrying_cost", "incoming_replacement", "margin",
  "buyer_demand", "manufacturer_incentives", "prior_model_year"];
const LOCAL_PRIORITIES = ["fishability", "family_comfort", "shallow_water_capability", "offshore_capability",
  "performance", "luxury_finish", "ease_of_ownership", "storage", "range", "payment_affordability",
  "technology_electronics"];
const MKTG_PRIORITIES = ["inventory_turns", "margin", "leads", "prior_model_year_cleanup",
  "carrying_cost_reduction", "buyer_demand", "oem_support"];

const SCHEMA = [
  {
    key: "market", title: "Your Market", desc: "Where and how your dealership sells. Skip anything you are unsure of.",
    fields: [
      { k: "primary_location", label: "Primary Location", type: "text" },
      { k: "selling_radius_miles", label: "Typical Selling Radius (miles)", type: "number", help: "Approximate distance most of your customers travel to purchase from your dealership." },
      { k: "market_type", label: "Primary Boating Market", type: "multi", options: MARKET_TYPES, help: "Select all that apply. Choosing more than one simply represents a mixed market." },
      { k: "use_cases", label: "Primary Boating Use Cases", type: "multi", options: USE_CASES },
      { k: "waterways", label: "Primary Local Waterways", type: "tags", placeholder: "e.g. Charlotte Harbor", help: "Enter multiple waterways separated by commas. Each entry will be saved as a removable tag. Example: Charlotte Harbor, Pine Island Sound, Boca Grande Pass" },
      { k: "boating_areas", label: "Important Boating Areas", type: "tags", placeholder: "e.g. Cayo Costa", help: "Enter multiple boating areas separated by commas. Each entry will be saved as a removable tag. Example: Cayo Costa, Boca Grande, local sandbars" },
      { k: "seasonal_notes", label: "Dealer Seasonal Notes", type: "textarea", help: "Describe seasonal patterns that affect what sells or how customers use boats. Example: Offshore fishing demand strengthens in spring, while family recreation becomes more important during summer." },
    ],
  },
  {
    key: "inventory_philosophy", title: "Inventory Priorities", desc: "Your preferences, kept separate from factual inventory data.",
    fields: [
      { k: "target_turn_days", label: "Target Inventory Turn (days)", type: "number", help: "Your preferred number of days to sell a typical inventory unit." },
      { k: "aged_after_days", label: "Consider Inventory Aged After (days)", type: "number", help: "The point when this dealership begins treating a boat as aged inventory and may want RecJet Mind to increase its priority." },
      { k: "prior_model_year_priority", label: "Prior Model Year Priority", type: "single", options: ["high", "normal", "low", "unknown"], help: "How aggressively should RecJet Mind prioritize remaining prior-model-year inventory?" },
      { k: "incoming_pressure", label: "Incoming Inventory Pressure", type: "single", options: ["high", "moderate", "low", "unknown"], help: "How much should incoming replacement inventory increase pressure to move similar units already in stock?" },
      { k: "priorities", label: "What You Generally Prioritize", type: "multi", options: INV_PRIORITIES, help: "Your general preference, kept separate from actual measured inventory facts." },
      { k: "notes", label: "Inventory Strategy Notes", type: "textarea", help: "Add any dealership-specific inventory strategy RecJet Mind should understand. Example: Move remaining 2026 models before 2027 inventory arrives." },
    ],
  },
  {
    key: "floorplan_defaults", title: "Floorplan & Carrying Cost", desc: "Dealer-level defaults. A unit-specific value always overrides these. Defaults are not confirmed unit-level lender data.",
    fields: [
      { k: "normally_floorplanned", label: "Inventory Normally Floorplanned", type: "single", options: ["yes", "no", "unknown"] },
      { k: "entries", label: "Brand / Lender Defaults", type: "entries", help: "Repeatable defaults for brands or lenders you floorplan with. These stay separate from confirmed unit-level information entered on each boat." },
      { k: "notes", label: "Floorplan Notes", type: "textarea", help: "Use this for common exceptions, unusual manufacturer programs, or floorplan practices not captured by the structured defaults." },
    ],
  },
  {
    key: "buyer_sales_profile", title: "Buyer & Sales Profile", desc: "References your Buyer Intelligence personas and canonical boat categories. It does not replace them.",
    fields: [
      { k: "strongest_personas", label: "Strongest Buyer Personas", type: "personas", help: "Select the Buyer Intelligence personas that best represent the customers this dealership most commonly serves. RecJet Mind uses these together with actual buyer signals and purchase-intent data." },
      { k: "local_buyer_segments", label: "Important Local Buyer Segments", type: "tags", placeholder: "e.g. Tournament Anglers", help: "Enter multiple local buyer segments separated by commas. Each entry will be saved as a removable tag. Example: Tournament Anglers, Seasonal Residents, Charter Captains" },
      { k: "strongest_categories", label: "Strongest Boat Categories", type: "multi", options: CATEGORIES },
      { k: "weak_categories", label: "Historically Weak Categories", type: "multi", options: CATEGORIES, help: "Select categories that historically move more slowly or require more effort in your market." },
      { k: "common_use_cases", label: "Common Buyer Use Cases", type: "multi", options: USE_CASES },
      { k: "trade_up_behavior", label: "Typical Trade-Up Behavior", type: "textarea", help: "Describe common trade-up patterns you see. Example: Many customers move from 20 to 22-foot bay boats into 24 to 26-foot offshore-capable models." },
      { k: "local_priorities", label: "Local Buyer Priorities", type: "multi", options: LOCAL_PRIORITIES },
      { k: "local_priorities_notes", label: "Buyer Priority Notes", type: "textarea", help: "Optional. Add dealer-specific nuance about what your buyers care about most." },
    ],
  },
  {
    key: "marketing_preferences", title: "Marketing Preferences", desc: "Your preferences, distinct from measured historical results.",
    fields: [
      { k: "organic_vs_paid", label: "Organic vs Paid Preference", type: "single", options: ["organic", "balanced", "paid", "unknown"], help: "This is the dealer's general preference. It does not replace measured campaign performance." },
      { k: "paid_radius_miles", label: "Typical Paid Campaign Radius (miles)", type: "number", help: "Typical geographic radius used for paid marketing campaigns." },
      { k: "paid_aggressiveness", label: "Paid Spend Aggressiveness", type: "single", options: ["conservative", "moderate", "aggressive", "unknown"] },
      { k: "monthly_paid_budget", label: "Monthly Paid Digital Advertising Budget", type: "currency", help: "Approximate amount this dealership is comfortable spending each month on paid digital advertising. Do not include OEM co-op or manufacturer support. Leave this blank if you have not set a budget, or enter 0 if you do not plan to spend dealer funds on paid digital advertising." },
      { k: "weather_sensitivity", label: "Weather Sensitivity", type: "single", options: ["low", "medium", "high", "unknown"], help: "How strongly should poor or favorable boating weather influence timing and paid-spend recommendations? High means weather should materially affect campaign timing and spend decisions." },
      { k: "recommend_budget", label: "Should RecJet Mind Recommend Budget?", type: "bool" },
      { k: "priorities", label: "What You Want to Prioritize", type: "multi", options: MKTG_PRIORITIES },
      { k: "notes", label: "Marketing Notes", type: "textarea", help: "Add dealership-specific marketing preferences or instructions not captured elsewhere." },
    ],
  },
];

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>
    {children}
  </button>
);

function TagsInput({ value, onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const tags = value || [];
  const add = (raw) => {
    const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...tags];
    for (const p of parts) if (!next.some((t) => t.toLowerCase() === p.toLowerCase())) next.push(p);
    onChange(next);
    setText("");
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(text); }
    else if (e.key === "Backspace" && !text && tags.length) onChange(tags.slice(0, -1));
  };
  return (
    <div className="rounded-md border border-slate-300 bg-white p-2 flex flex-wrap gap-2 focus-within:border-navy" data-testid={testid}>
      {tags.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1 bg-navy text-white text-xs rounded-full pl-2.5 pr-1.5 py-1" data-testid={`${testid}-tag-${i}`}>
          {t}
          <button type="button" onClick={() => onChange(tags.filter((_, idx) => idx !== i))} data-testid={`${testid}-remove-${i}`} className="hover:text-gold">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} onBlur={() => text && add(text)}
        placeholder={tags.length ? "" : (placeholder || "Type and press Enter")} data-testid={`${testid}-input`}
        className="flex-1 min-w-[150px] outline-none text-sm bg-transparent py-0.5" />
    </div>
  );
}

function EntriesEditor({ value, onChange }) {
  const rows = value || [];
  const upd = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => onChange([...rows, { brand: "", lender: "", free_floor_days: "", first_curtailment_timing_days: "", curtailment_amount_pct: "", source: "dealer", last_verified_at: "", oem_program_notes: "" }]);
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3" data-testid="floorplan-entries">
      {rows.map((r, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Brand" value={r.brand || ""} onChange={(e) => upd(i, "brand", e.target.value)} data-testid={`fp-brand-${i}`} />
            <Input placeholder="Lender" value={r.lender || ""} onChange={(e) => upd(i, "lender", e.target.value)} data-testid={`fp-lender-${i}`} />
            <Input type="number" placeholder="Free floor (days)" value={r.free_floor_days ?? ""} onChange={(e) => upd(i, "free_floor_days", e.target.value)} data-testid={`fp-freefloor-${i}`} />
            <Input type="number" placeholder="First curtailment (days)" value={r.first_curtailment_timing_days ?? ""} onChange={(e) => upd(i, "first_curtailment_timing_days", e.target.value)} data-testid={`fp-curtail-${i}`} />
            <Input type="number" placeholder="Curtailment amount %" value={r.curtailment_amount_pct ?? ""} onChange={(e) => upd(i, "curtailment_amount_pct", e.target.value)} data-testid={`fp-pct-${i}`} />
            <Input type="date" placeholder="Last verified" value={r.last_verified_at || ""} onChange={(e) => upd(i, "last_verified_at", e.target.value)} data-testid={`fp-verified-${i}`} />
          </div>
          <Input placeholder="OEM program notes" value={r.oem_program_notes || ""} onChange={(e) => upd(i, "oem_program_notes", e.target.value)} data-testid={`fp-notes-${i}`} />
          <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => del(i)} data-testid={`fp-del-${i}`}><Trash2 className="w-4 h-4 mr-1" />Remove</Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="fp-add"><Plus className="w-4 h-4 mr-1" />Add brand / lender default</Button>
    </div>
  );
}

export default function DealerIntelligenceProfile() {
  const [profile, setProfile] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [dealerLoc, setDealerLoc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/dealer-intelligence/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/intelligence/personas").then(({ data }) => setPersonas(data || [])).catch(() => {});
    api.get("/dealership").then(({ data }) => setDealerLoc([data?.city, data?.state].filter(Boolean).join(", "))).catch(() => {});
  }, []);

  // Map any legacy persona names stored as free text to the real Buyer Intelligence persona IDs.
  useEffect(() => {
    if (!profile || personas.length === 0) return;
    const ids = new Set(personas.map((p) => p.id));
    const byName = new Map(personas.map((p) => [String(p.name).toLowerCase(), p.id]));
    const cur = profile.buyer_sales_profile?.strongest_personas || [];
    const mapped = [];
    for (const val of cur) {
      if (ids.has(val)) { if (!mapped.includes(val)) mapped.push(val); continue; }
      const id = byName.get(String(val).toLowerCase());
      if (id && !mapped.includes(id)) mapped.push(id);
    }
    const changed = mapped.length !== cur.length || mapped.some((v, i) => v !== cur[i]);
    if (changed) setProfile((p) => ({ ...p, buyer_sales_profile: { ...p.buyer_sales_profile, strongest_personas: mapped } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personas, profile?.dealership_id]);

  const setVal = (sec, k, v) => setProfile((p) => ({ ...p, [sec]: { ...p[sec], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      for (const s of SCHEMA) body[s.key] = profile[s.key];
      const { data } = await api.put("/dealer-intelligence/profile", body);
      setProfile(data);
      toast.success("Dealer profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save profile"); }
    finally { setSaving(false); }
  };

  if (!profile) return <div className="p-8 text-muted-foreground" data-testid="di-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const renderField = (sec, f) => {
    const v = profile[sec]?.[f.k];
    if (f.type === "text") {
      const ph = f.k === "primary_location" ? (dealerLoc ? `Defaults to ${dealerLoc} from Settings` : "") : (f.placeholder || "");
      return <Input value={v || ""} placeholder={ph} onChange={(e) => setVal(sec, f.k, e.target.value)} data-testid={`di-${sec}-${f.k}`} />;
    }
    if (f.type === "number") return <Input type="number" value={v ?? ""} onChange={(e) => setVal(sec, f.k, e.target.value === "" ? null : Number(e.target.value))} data-testid={`di-${sec}-${f.k}`} />;
    if (f.type === "currency") return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">$</span>
        <Input type="number" min="0" className="pl-7" value={v ?? ""} placeholder="Leave blank if not set"
          onChange={(e) => setVal(sec, f.k, e.target.value === "" ? null : Number(e.target.value))} data-testid={`di-${sec}-${f.k}`} />
      </div>
    );
    if (f.type === "textarea") return <Textarea rows={3} value={v || ""} onChange={(e) => setVal(sec, f.k, e.target.value)} data-testid={`di-${sec}-${f.k}`} />;
    if (f.type === "tags") return <TagsInput value={v} placeholder={f.placeholder} onChange={(t) => setVal(sec, f.k, t)} testid={`di-${sec}-${f.k}`} />;
    if (f.type === "bool") return (
      <div className="flex flex-wrap gap-2">
        {[["Yes", true], ["No", false], ["Unknown", null]].map(([lbl, val]) => (
          <Chip key={lbl} active={v === val} onClick={() => setVal(sec, f.k, val)} testid={`di-${sec}-${f.k}-${lbl.toLowerCase()}`}>{lbl}</Chip>
        ))}
      </div>
    );
    if (f.type === "single") return (
      <div className="flex flex-wrap gap-2">
        {f.options.map((o) => <Chip key={o} active={v === o} onClick={() => setVal(sec, f.k, v === o ? null : o)} testid={`di-${sec}-${f.k}-${slug(o)}`}>{optLabel(o)}</Chip>)}
      </div>
    );
    if (f.type === "multi") {
      const arr = v || [];
      const extras = arr.filter((x) => !f.options.includes(x));
      return (
        <div className="flex flex-wrap gap-2">
          {[...f.options, ...extras].map((o) => (
            <Chip key={o} active={arr.includes(o)} testid={`di-${sec}-${f.k}-${slug(o)}`}
              onClick={() => setVal(sec, f.k, arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])}>{optLabel(o)}</Chip>
          ))}
        </div>
      );
    }
    if (f.type === "personas") {
      const arr = v || [];
      if (personas.length === 0) return <p className="text-xs text-muted-foreground" data-testid={`di-${sec}-${f.k}-empty`}>No Buyer Intelligence personas available yet. Visit Buyer Intelligence to set them up.</p>;
      return (
        <div className="flex flex-wrap gap-2" data-testid={`di-${sec}-${f.k}`}>
          {personas.map((p) => (
            <Chip key={p.id} active={arr.includes(p.id)} testid={`di-persona-${p.id}`}
              onClick={() => setVal(sec, f.k, arr.includes(p.id) ? arr.filter((x) => x !== p.id) : [...arr, p.id])}>{p.name}</Chip>
          ))}
        </div>
      );
    }
    if (f.type === "entries") return <EntriesEditor value={v} onChange={(rows) => setVal(sec, f.k, rows)} />;
    return null;
  };

  const completed = profile.meta?.completed_sections || [];

  return (
    <div className="space-y-6 fade-up max-w-3xl" data-testid="dealer-intelligence-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center shrink-0"><Anchor className="w-6 h-6 text-gold" /></div>
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">RecJet Mind Dealer Profile</h1>
          <p className="text-sm text-muted-foreground mt-1">A short, guided profile of your dealership so future RecJet Mind recommendations reflect how you actually sell. Everything is optional, you can skip what you do not know, and you can edit this anytime.</p>
        </div>
      </div>

      {SCHEMA.map((s) => (
        <section key={s.key} className="rounded-xl border border-slate-200 bg-white p-6 space-y-6" data-testid={`di-section-${s.key}`}>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-navy text-lg">{s.title}</h2>
              {completed.includes(s.key) && <span className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Saved</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
          </div>
          <div className="grid grid-cols-1 gap-6">
            {s.fields.map((f) => (
              <div key={f.k} className="space-y-2">
                <Label className="text-sm font-medium text-navy">{f.label}</Label>
                {f.help && <p className="text-xs text-muted-foreground leading-relaxed">{f.help}</p>}
                {renderField(s.key, f)}
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light text-white shadow-lg" data-testid="di-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Dealer Profile
        </Button>
      </div>
    </div>
  );
}
